import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { getUserPrivileges, getUserProfile } from '../service/account';
import {
    getAgentInfo,
    getMlsMembership,
    getOfficeInfo
} from '../service/mlsInfo';
import { getTimeIntervals, retryEndpoint } from '../service/misc';
import {
    getAgentLists,
    getMarketAreaLists,
    getOfficeLists
} from '../service/savedLists';
import {
    downloadFileTypes as dft,
    ISO_MIDNIGHT,
    modules,
    ownerTypes,
    searchRoutes,
    searchTerms,
    tabNameMap,
    agentProductionTerms,
} from '../constants';
import { getPDF, saveAgentSearchList, updateSavedAgentList } from '../service/service-gateway';
import { getOwnerIdByTokenInfo } from '../constants/auth';
import { useAuthStore } from './auth/store';
import { useSavedAgentsStore as useSavedAgentsStoreImported } from './savedAgents/store';
import { runningApp } from '../constants/app';
import { errorMessaging as errorMsg } from '../market-dynamics/constants';
import {
    agentDetailReportConfig,
    downloadPDF,
    flattenTable,
    getPdfClassname,
    renderComponentToHtml,
    reportConfig,
    transformTableView,
} from '../market-dynamics/helpers/pdf';
import { paginationConfig } from '../market-dynamics/constants/pdf';
import { useMarketDynamicsStore } from './marketDynamics/store';
import Report from '../market-dynamics/components/common/pdf/Report';
import { useSearchStore as useSearchStoreImported } from './search/store';
import { listingOptions } from '../market-dynamics/constants/listingViews/overview';
import html2canvas from 'html2canvas';
import { useComparisonSetsStore } from './comparisonSets/store';
import { getListingStatus } from '../market-share/helpers/listingStatus';
import { usePDFStore } from './pdf/store';
import { backButtonConfig } from '../common/helpers/routes';
import { actions as mDbreakdownActions } from './marketDynamics/breakdowns/actions';
import { getPendoDataLayers } from '../common/helpers/pendo';
import { createLastXDaysInterval } from '../common/helpers/date';
import { agentProfileTabNamesByIndex, favoriteAgentTerms } from '../agent-production/constants/agentProductionConstants';
import { createTransactionCoveragePDFView, inventoryView } from '../common/components/table/pdf/tableViews';
import { tableViews } from '../common/components/table/agGrid/tableViews';
import { getDataWithTotalsRow } from '../agent-production/helpers/agentProductionHelpers';

// Hack to keep current import in every file that uses this store
export const useSearchStore = useSearchStoreImported;
export const useSavedAgentsStore = useSavedAgentsStoreImported;

let agentProfileStore = set => ({
    areaList: undefined,
    selectedAgentId: undefined,
    selectedMlsId: undefined,
    selectedArea: undefined,
    areaType: undefined,
    tableType: undefined,
    intervalType: undefined,
    isUnits: undefined,
    agentCoverageListingTab: undefined,
    currentTab: undefined,
    displayMode: undefined,
    timePeriod: undefined,
    inventoryTableType: agentProductionTerms.forSaleListing,
    setAreaList: (list) => set({ areaList: list }),
    setSelectedAgentId: (agentId) => set({ selectedAgentId: agentId }),
    setSelectedMlsId: (mlsId) => set({ selectedMlsId: mlsId }),
    setSelectedArea: (area) => set({ selectedArea: area }),
    setAreaType: (areaType) => set({ areaType: areaType }),
    setTableType: (tableType) => set({ tableType: tableType }),
    setIntervalType: (intervalType) => set({ intervalType: intervalType }),
    setIsUnits: (isUnits) => set({ isUnits: isUnits }),
    setAgentCoverageListingTab: (tab) => set({ agentCoverageListingTab: tab }),
    setCurrentTab: (tab) => set({ currentTab: tab }),
    setDisplayMode: (displayMode) => set({ displayMode }),
    setInventoryTableType: (inventoryTableType) => set({ inventoryTableType }),
    setTimePeriod: (timePeriod) => set({ timePeriod: timePeriod })
});

export const useAgentProfileStore = create(
    devtools(
        persist(
            agentProfileStore,
            {
                name: 'ap_state',
                storage: createJSONStorage(() => localStorage)
            }
        )
    )
);

const initialCommonState = {
    timeIntervals: undefined,
    selectedModule: undefined,
    membership: undefined,
    profile: undefined,
    mlsProviders: undefined,
    officeInfo: undefined,
    agentInfo: undefined,
    popoverIsOpen: false,
    apiError: undefined,
    menuIsOpen: undefined,
    isSideNavExpanded: true,
    privileges: undefined,
    marketAreaLists: undefined,
    officeLists: undefined,
    agentLists: undefined,
    accountInfo: undefined,
    userInfo: undefined,
    selectedBeginValue: undefined,
    selectedEndValue: undefined,
    agentListToUpdate: undefined,
    lastUpdatedAgentList: undefined,
    popoverDataForAddAgentList: {},
    mlsList: undefined,
    backNavbar: { title: undefined, backLinkTitle: undefined },
    getSearchResults: null,
    selectedRowsIds: [],
    agentDetailNavigation: undefined,
    propertyTypes: undefined,
};

let commonStore = (set, get) => ({
    ...initialCommonState,
    ...mDbreakdownActions(set),
    getPrivileges: async (accountId, userId) => {
        let response;
        try {
            response = await getUserPrivileges(accountId, userId);
            if (!response.error) {
                set({ privileges: response });
            }
        } catch (err) {
            set({ error: err.message });
        }
        return response;
    },
    getUserAndOfficeInfo: () => {
        const searchStore = useSearchStore.getState();
        const selectedModule = searchStore.selectedModule;
        const selectedMls = searchStore[selectedModule]?.selectedMls || {mlsId: get().agentInfo?.[0]?.mlsAgentId.mlsId};
        const userInfo = get().userInfo;
        const tokenInfo = useAuthStore.getState().tokenInfo;
        const officeInfo = get().officeInfo;
        const emailAddress = userInfo ? userInfo.email : tokenInfo.email;
        const associatedOffice = [...officeInfo]?.sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()))
            ?.filter(o => o.isOfficeActive)?.find(o => o.mlsOfficeId?.mlsId === selectedMls?.mlsId);
        const { name='', address='', phoneList='' } = associatedOffice || {};
        const { street1='', city='', stateCode='', zipCode='' } = address || {};
        const phoneNumber = phoneList?.length > 0 ? phoneList[0].phoneNumber : agentProductionTerms.notApplicableAbbrv;
        const officeId = get().agentInfo?.[0]?.office?.mlsOfficeId?.officeId;
        return { emailAddress, name, street1, city, stateCode, zipCode, phoneNumber, officeId };
    },
    getAssociatedBrokerageId: () => {
        const searchStore = useSearchStore.getState();
        const selectedModule = searchStore.selectedModule;
        const officeInfo = get().officeInfo;
        const selectedMls = searchStore[selectedModule]?.selectedMls || { mlsId: get().agentInfo?.[0]?.mlsAgentId.mlsId };
        const associatedOffice = [...(officeInfo ?? [])].sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()))
            .filter(o => o.isOfficeActive).find(o => o.mlsOfficeId?.mlsId === selectedMls?.mlsId);
        return associatedOffice?.mlsBrokerId?.brokerId;
    },

    addAgentListsCheckedValues: (listId) => {
        const newCheckedValues = { ...get().agentListsCheckedValues };
        newCheckedValues[listId] = true;
        set({ agentListsCheckedValues: newCheckedValues });
    },
    removeAgentListsCheckedValues: (listId) => {
        const newCheckedValues = { ...get().agentListsCheckedValues };
        newCheckedValues[listId] = false;
        set({ agentListsCheckedValues: newCheckedValues });
    },
    handleUpdateAgentNameSelectedList: async (listId, agentIdToAdd, listName, agentNameSearch, setListInSearchByAgentSearch) => {
        const shouldUpdateAgentNameSelectedList = (moduleSearch) => {
            const dataSourceId = moduleSearch?.search?.searchCriteria?.criteria?.realEstateDatasourceIdsWithFilters[0];
            const hasSearchCriteria = !!moduleSearch?.search?.searchCriteria;
            const hasSearchMlsId = !!moduleSearch?.search?.mlsId;
            const searchListId = dataSourceId?.idFiltering[0]?.listId;
            const searchListIdMatch = searchListId === listId;
            const idValues = dataSourceId?.idFiltering[0]?.idValues;
            const hasAgentId = idValues?.includes(agentIdToAdd);

            if (hasSearchCriteria && !!hasSearchMlsId && !!searchListId && searchListIdMatch && !!idValues.length) {
                if (hasAgentId) {
                    const updatedIdValues = idValues.filter(id => id !== agentIdToAdd);
                    setListInSearchByAgentSearch(updatedIdValues, searchListId);
                } else {
                    setListInSearchByAgentSearch([...idValues, agentIdToAdd], searchListId);
                }
            }
        };
        let modifiedAgentList = {};
        const selectedAgentList = get().agentLists.find(list => list.agentListId === listId);
        const updateAgentList = async (modifiedAgentList, listName, listId) => {
            set({ agentListToUpdate: listName });
            const response = await updateSavedAgentList(modifiedAgentList);
            const updatedAgentLists = get().agentLists.map((list) =>
                list.agentListId === response.agentListId
                    ? response
                    : list
            );
            set({ agentLists: updatedAgentLists, agentListToUpdate: '', lastUpdatedAgentList: listId });

            const savedAgentsModule = modules.proficiencyMetrics.savedAgents;
            const agentNameModule = modules.proficiencyMetrics.agents;

            const savedAgentsSelectedListId = useSavedAgentsStore.getState().selectedListByModule[savedAgentsModule]?.agentListId;
            const agentNameSelectedListId = useSavedAgentsStore.getState().selectedListByModule[agentNameModule]?.agentListId;

            if (savedAgentsSelectedListId === listId) {
                useSavedAgentsStore.getState().setSelectedListByModule(savedAgentsModule, response);
                useSavedAgentsStore.getState().markListAsDirty(listId);
            }

            if (agentNameSelectedListId === listId) {
                useSavedAgentsStore.getState().setSelectedListByModule(agentNameModule, response);
                useSavedAgentsStore.getState().markListAsDirty(listId);
            }
        };

        // This is remove
        if (selectedAgentList.agentIds.includes(agentIdToAdd)) {
            modifiedAgentList = {
                ...selectedAgentList,
                agentIds: selectedAgentList.agentIds.filter(agentId => agentId !== agentIdToAdd)
            };
            get().removeAgentListsCheckedValues(listId);
            try {
                await updateAgentList(modifiedAgentList, listName, listId);
            } catch (err) {
                get().addAgentListsCheckedValues(listId);
                set({ error: err.message });
            }
            // This is add
        } else {
            modifiedAgentList = {
                ...selectedAgentList,
                agentIds: [...selectedAgentList.agentIds, agentIdToAdd]
            };
            get().addAgentListsCheckedValues(listId);
            try {
                await updateAgentList(modifiedAgentList, listName, listId);
            } catch (err) {
                get().removeAgentListsCheckedValues(listId);
                set({ error: err.message });
            }
        }
        shouldUpdateAgentNameSelectedList(agentNameSearch);
    },
    createAgentsList: async (mlsId, listName, agentIds) => {
        const tokenInfo = useAuthStore.getState().tokenInfo;
        const tokenType = useAuthStore.getState().tokenType;
        const ownerId = getOwnerIdByTokenInfo[tokenType](tokenInfo);
        const ownerType = ownerTypes[tokenType.toLowerCase()] || ownerTypes.user;
        const resolvedAgentIds = Array.isArray(agentIds) ? [...new Set(agentIds)] : (agentIds ? [agentIds] : []);
        const newAgentList = {
            name: listName,
            application: runningApp,
            ownerType,
            ownerId,
            mlsId: mlsId,
            agentIds: resolvedAgentIds
        };
        try {
            const response = await saveAgentSearchList(newAgentList, true);
            await get().getAgentLists(ownerId);
            return response;
        } catch (err) {
            set({ error: err.message });
            throw err;
        }
    },
    toggleAgentListsPopover: () => {
        set({
            popoverDataForAddAgentList: prevState => ({
                ...prevState.popoverDataForAddAgentList,
                popoverIsOpen: !prevState.popoverDataForAddAgentList.popoverIsOpen
            })
        });
    },
    clearAgentListPopover: () => set({
        popoverDataForAddAgentList: {}
    }),
    setMlsProviders: mlsList => set({ mlsProviders: mlsList }),
    setSelectedBeginValue: selectedBeginValue => set({ selectedBeginValue: selectedBeginValue }),
    setSelectedEndValue: selectedEndValue => set({ selectedEndValue: selectedEndValue }),
    getTimeIntervals: async () => {
        try {
            const response = await retryEndpoint({ fetchFunction: getTimeIntervals });
            const todaysDate = new Date();
            const newSuggestedAgentsLastXDayIntervals = [60, 90].map(days => createLastXDaysInterval(days, todaysDate));
            set({ timeIntervals: [ ...response, ...newSuggestedAgentsLastXDayIntervals] });
        } catch (err) {
            set({ error: err.message });
        }
    },
    getUserProfile: async (username) => {
        let response;
        try {
            response = await getUserProfile(username);
            set({ profile: response });
        } catch (err) {
            set({ error: err.message });
        }
        return response;
    },
    getMembership: async (mlsIdList) => {
        try {
            const response = await getMlsMembership(mlsIdList);
            set({ membership: Array.isArray(response) ? response : [] });
        } catch (err) {
            set({ error: err.message });
        }
    },
    getOfficeInfo: async (officeList) => {
        let response;
        try {
            response = await retryEndpoint({ fetchFunction: getOfficeInfo, payload: officeList });
            set({ officeInfo: response });
        } catch (err) {
            set({ error: err.message });
        }
        return response;
    },
    getAgentInfo: async (agentList) => {
        let response;
        try {
            response = await getAgentInfo(agentList);
            set({ agentInfo: response });
        } catch (err) {
            set({ error: err.message });
        }
        return response;
    },
    getMarketAreaLists: async (userId) => {
        try {
            set({ isLoading: true });
            const response = await getMarketAreaLists(userId);
            set({ isLoading: false, marketAreaLists: response?.areas });
        } catch (err) {
            set({ isLoading: false, error: err.message });
        }
    },
    getOfficeLists: async (userId) => {
        try {
            set({ isLoading: true });
            const response = await getOfficeLists(userId);
            set({ isLoading: false, officeLists: response?.list });
        } catch (err) {
            set({ isLoading: false, error: err.message });
        }
    },
    getAgentLists: async (userId) => {
        try {
            const response = await getAgentLists(userId);
            const filtered = (response || []).filter(list =>
                list?.name?.toLowerCase() !== favoriteAgentTerms.reservedListName.toLowerCase()
            );
            set({ isLoading: false, agentLists: filtered });
        } catch (err) {
            set({ error: err.message });
        }
    },
    toggleMenuIsOpen: (menuIsOpen) => set({ menuIsOpen }),
    setIsSideNavExpanded: (isSideNavExpanded) => set({ isSideNavExpanded }),
    resetStore: () => {
        set(initialCommonState);
    },
    setAccountInfo: (accountInfo) => {
        set({ accountInfo });
    },
    setUserInfo: (userInfo) => {
        set({ userInfo });
    },
    captureChartImage: async () => {

        const overviewStatus = useMarketDynamicsStore.getState()?.overviewStatus;
        const module = useSearchStore.getState()?.selectedModule;
        const listingViewId = useSearchStore.getState()[module]?.listingViewId;
        const isOverview = listingViewId === listingOptions.overview.value;
    
        const container = document.createElement('div');
        const chartArea = document.querySelector(getPdfClassname(isOverview, overviewStatus.currentTab, true));
        const keyInfoArea = document.querySelector('.key-info-capture-area');
        
        container.style.position = 'absolute';
        container.style.left = '-8000px';
        container.style.top = '-8000px';
    
        const clonedChartArea = chartArea?.cloneNode(true);
        const clonedKeyInfoArea = keyInfoArea?.cloneNode(true);
    
        if (clonedChartArea){
            clonedChartArea.style.position = 'static';
            clonedChartArea.style.left = 'auto';
            clonedChartArea.style.top = 'auto';
            container.appendChild(clonedChartArea);
        }
    
        if (clonedKeyInfoArea){
            clonedKeyInfoArea.style.position = 'static';
            clonedKeyInfoArea.style.left = 'auto';
            clonedKeyInfoArea.style.top = 'auto';
            container.appendChild(clonedKeyInfoArea);
        }
    
        document.body.appendChild(container);
        const options = { scale: 2 };
        const canvas = await html2canvas(container, options);
        document.body.removeChild(container);
        return canvas.toDataURL('image/png');
    },
    captureDashboardImage: async () => {

        const container = document.createElement('div');
        const dashboardArea =  document.querySelector('.dashboard-capture-area');
        
        container.style.position = 'absolute';
        container.style.left = '-10000px';
        container.style.top = '-10000px';

        const clonedDashboardArea = dashboardArea?.cloneNode(true);

        if (clonedDashboardArea){
            clonedDashboardArea.style.position = 'static';
            clonedDashboardArea.style.left = 'auto';
            clonedDashboardArea.style.top = 'auto';
            container.appendChild(clonedDashboardArea);
        }
    
        document.body.appendChild(container);
        const options = { scale: 2 };
        const canvas = await html2canvas(container, options);
        document.body.removeChild(container);
        return canvas.toDataURL('image/png');
    },
    handlePDFGeneration: async ({
        renderProps, 
        module, 
        listingView,
        shouldCaptureChart = false,
        shouldCaptureDashboard = false,
        exportDetails,
    }) => {
        try {
            await new Promise(resolve => setTimeout(resolve, 600));
            const chartImage = shouldCaptureChart ? await get().captureChartImage() : null;
            const dashboardImage = shouldCaptureDashboard ? await get().captureDashboardImage() : null;
            const htmlDoc = renderComponentToHtml(Report, {
                ...renderProps,
                exportDetails,
                chartImage,
                dashboardImage,
            });
            const streamResponse = new Response(await getPDF(htmlDoc, paginationConfig));
            const { config } = renderProps;
            await downloadPDF(streamResponse, module, listingView, exportDetails, config);
        } catch (error) {
            console.error(errorMsg.convertToPdf, error);
        } finally {
            usePDFStore.getState().setDownloadModalStatus({
                isPDFLoading: false,
                fileType: dft.csv,
                isOpen: false
           });
        }
    },
    handleMarketDynamicsPDF: ( module, loadedSearchCriteria) => {
        usePDFStore.getState().setDownloadModalStatus({
            isPDFLoading: true,
            fileType: dft.pdf
        });
        const searchResults = get().getSearchResults();
        const searchStore = useSearchStore.getState();
        if (!searchResults) throw new Error('Invalid Parameter: getSearchResults should be set from the results page');
        const defaultBeginValue = searchResults ? searchResults[searchResults?.length - 1]?.timePeriodStart : '';
        const defaultEndValue = searchResults ? searchResults[0]?.timePeriodEnd : '';
        const tableView = useMarketDynamicsStore.getState()?.tableView;
        const shouldShowChart = searchStore[module]?.shouldShowChart;
        const listingViewId = searchStore[module]?.listingViewId;
        const isOverview = listingViewId === listingOptions.overview.value;
        const marketIndicatorContent = isOverview && useMarketDynamicsStore.getState()?.marketIndicatorContent;
        const config = reportConfig[module];

        const renderProps = {       
            listingViewId,
            loadedSearchCriteria,
            module,
            searchResults,
            selectedBeginValue: isOverview ? defaultBeginValue : get().selectedBeginValue,
            selectedEndValue: isOverview ? defaultEndValue : get().selectedEndValue,
            selectedMls: searchStore[module]?.selectedMls,
            timeFrameTitle: searchStore[module]?.timeFrameTitle,
            tableView,
            marketIndicatorContent,
            config,
        }
        get().handlePDFGeneration({
            renderProps,
            module,
            listingView: listingViewId,
            shouldCaptureChart: shouldShowChart,
            shouldCaptureDashboard: isOverview
        });
    },
    handleMarketSharePDF: (module, loadedSearchCriteria) => {
        usePDFStore.getState().setDownloadModalStatus({
             isPDFLoading: true,
             fileType: dft.pdf
        });
        const searchStore = useSearchStore.getState();
        const pdfStore = usePDFStore.getState();
        const bottomRowData = pdfStore[module].pinnedBottomRowData?.[0];
        const searchResults = [...pdfStore[module].results, bottomRowData];
        const tableView = transformTableView(pdfStore[module].columnDefs);
        const listingViewTitle = pdfStore[module].listingViewTitle;
        const listingTypeId = searchStore[module].listingType;
        const timeIntervals = useCommonStore.getState()?.timeIntervals;
        const period = loadedSearchCriteria.timePeriod.intervalType;
        const { from, to } = timeIntervals.find(interval => interval.intervalType === period)?.last ?? 
        { 
          from: `${loadedSearchCriteria.timePeriod?.startDate}${ISO_MIDNIGHT}`,
          to: `${loadedSearchCriteria.timePeriod?.endDate}${ISO_MIDNIGHT}`
        };
        const isComparisonSetView = useComparisonSetsStore.getState()?.isComparisonSetView;
        const mlsObjectId = loadedSearchCriteria.realEstateDatasourceIdsWithFilters[0].mlsObjectId;
        const selectedComparisonSet = useComparisonSetsStore.getState().comparisonSets?.list?.find(cs => cs.id === mlsObjectId);
        const areaTypeTitle = searchStore[module].areaTypeTitle.mainTitle.replace(`${searchTerms.areaType}: `, "");
        const listingStatus = getListingStatus(module, loadedSearchCriteria);
        const selectedOfficeList = searchStore[module].selectedOfficeList;
        const officesTitle =searchStore[module].officesTitle;
        const officeListName = selectedOfficeList === officesTitle.mainTitle ? selectedOfficeList : searchTerms.myOffices;
        const config = reportConfig[module];
        const shouldShowChart = searchStore[module]?.shouldShowChart;
        const renderProps = {
            listingViewTitle,
            loadedSearchCriteria,
            module,
            searchResults,
            selectedBeginValue: from,
            selectedEndValue: to,
            selectedMls: searchStore[module]?.selectedMls,
            timeFrameTitle: searchStore[module]?.timeFrameTitle,
            listingTypeId,
            isComparisonSetView,
            selectedComparisonSet,
            areaTypeTitle,
            listingStatus,
            tableView,
            officeListName,
            config,
        }
        get().handlePDFGeneration({
            renderProps,
            module,
            listingView: listingViewTitle,
            shouldCaptureChart: shouldShowChart,
    });
    },
    handleAgentMovementPDF: (module, loadedSearchCriteria) => {
        usePDFStore.getState().setDownloadModalStatus({
            isPDFLoading: true,
            fileType: dft.pdf
        });

        const searchStore = useSearchStore.getState();
        const selectedOfficeList = searchStore[module].selectedOfficeList;
        const officesTitle =searchStore[module].officesTitle;
        const officeListName = selectedOfficeList === officesTitle.mainTitle ? selectedOfficeList : null;
        const timeIntervals = useCommonStore.getState()?.timeIntervals;
        const period = loadedSearchCriteria.timePeriod.intervalType;
        const { from, to } = timeIntervals.find(interval => interval.intervalType === period)?.last ?? 
        { 
          from: `${loadedSearchCriteria.timePeriod?.startDate}${ISO_MIDNIGHT}`,
          to: `${loadedSearchCriteria.timePeriod?.endDate}${ISO_MIDNIGHT}`
        };
        const pdfStore = usePDFStore.getState();
        const bottomRowData = {
            ...searchStore[module].totals,
            officeName: searchTerms.totals
        };
        const sortedResults = [...pdfStore[module].results].sort((a, b) => b.agentsLost - a.agentsLost);
        const searchResults = [...sortedResults, bottomRowData];
        const tableView = flattenTable(pdfStore[module].columnDefs);
        const config = reportConfig[module];
        const renderProps = {
            module,
            selectedMls: searchStore[module]?.selectedMls,
            loadedSearchCriteria,
            officeListName,
            selectedBeginValue: from,
            selectedEndValue: to,
            searchResults,
            tableView,
            config,
        };
        get().handlePDFGeneration({
            renderProps,
            module,
            shouldCaptureChart: true,
        })
    },
    handleAgentDetailsPDF: (module, loadedSearchCriteria) => {

        const pdfStore = usePDFStore.getState();

        pdfStore?.setDownloadModalStatus({
            isPDFLoading: true,
            fileType: dft.pdf
        });

        const searchStore = useSearchStore.getState();
        const productionSummary = pdfStore?.productionSummary;
        const selectedTabName = agentProfileTabNamesByIndex[pdfStore.agentProfileTabIndex];
        const exportDetails = pdfStore?.agentProfileExportDetails;
        const agentProfileTimePeriod = pdfStore?.agentProfileTimePeriod;
        const timeIntervals = useCommonStore.getState()?.timeIntervals;
        const { from, to } = timeIntervals.find(interval => interval.intervalType === agentProfileTimePeriod)?.last ??
            {
                from: `${loadedSearchCriteria.timePeriod?.startDate}${ISO_MIDNIGHT}`,
                to: `${loadedSearchCriteria.timePeriod?.endDate}${ISO_MIDNIGHT}`
            };
        const selectedAgentProfileArea = pdfStore?.agentProfileArea;
        const reportType = tabNameMap[selectedTabName];
        const config = agentDetailReportConfig[reportType];
        const transactionCoverageData = pdfStore?.areasData
            .map((area, index) => ({ ...area, rank: index + 1 }))
            .filter((area) => area.checked === true);
        const excludedTransactionCoverageFields = ['rank', 'area'];
        const agentMetricsMlsId = useCommonStore.getState().agentInfo?.[0]?.mlsAgentId.mlsId;
        const agentMetricsShortDescription = useCommonStore.getState().membership?.find(m => m.mlsId === agentMetricsMlsId)?.shortDescription || '';

        const settingsByTab = {
            [agentProfileTabNamesByIndex[0]]: {
                searchResults: pdfStore?.agentProfileProductionTableData || [],
                tableView: tableViews.agentDetailProduction,
            },
            [agentProfileTabNamesByIndex[1]]: {
                searchResults: pdfStore?.agentProfileInventoryTableData || [],
                tableView: inventoryView,
            },
            [agentProfileTabNamesByIndex[2]]: {
                searchResults: getDataWithTotalsRow(transactionCoverageData, excludedTransactionCoverageFields) || [],
                tableView: createTransactionCoveragePDFView(selectedAgentProfileArea),
            },
        };

        const renderProps = {
            module,
            selectedMls: searchStore[module]?.selectedMls || {shortDescription: agentMetricsShortDescription},
            loadedSearchCriteria,
            selectedTabName,
            selectedBeginValue: from,
            selectedEndValue: to,
            config,
            selectedAgentProfileArea,
            searchResults: settingsByTab[selectedTabName]?.searchResults,
            tableView: settingsByTab[selectedTabName]?.tableView,
            productionSummary,
        };
        get().handlePDFGeneration({
            renderProps,
            module,
            shouldCaptureChart: true,
            exportDetails,
        });
    },
    handleConvertToPDF: () => {
        const searchStore = useSearchStore.getState();
        const module = searchStore?.selectedModule;
        const loadedSearchCriteria = searchStore[module]?.loadedSearchCriteria;
        if (searchRoutes.agentDetail === window.location.pathname || module === modules.agentProduction.agentProduction) {
            get().handleAgentDetailsPDF(module, loadedSearchCriteria);
            return;
        }

        if (module === modules.marketDynamics.marketDynamics){
          get().handleMarketDynamicsPDF(module, loadedSearchCriteria);
        }

        if (module === modules.marketShare.totals || module === modules.marketShare.coverage){
          get().handleMarketSharePDF(module, loadedSearchCriteria);  
        }

        if (module === modules.proficiencyMetrics.agentMovement){
            get().handleAgentMovementPDF(module, loadedSearchCriteria);
        }
    },
    handleBackNavigation: (route, history, altBackRoute) => {
        const navigationAction = backButtonConfig[route];
        navigationAction?.(history, altBackRoute);
    },
    setPendoDataLayers: ({ tokenInfo, selectedAccount, isLegacyUser, runningApp, productFlags, userAccounts, ownerId }) => {
        const pendoDataLayers = getPendoDataLayers({ tokenInfo, selectedAccount, isLegacyUser, runningApp, productFlags, userAccounts, ownerId });
        set({ pendoDataLayers });
    },
    setAgentDetailNavigation: (updates) => {
        set(prevState => ({
            ...prevState,
            agentDetailNavigation: {
                ...prevState.agentDetailNavigation,
                ...updates
            }
        }));
    },
});

export const useCommonStore = create(
    devtools(
        persist(
            commonStore,
            {
                name: 'common_state',
                storage: createJSONStorage(() => sessionStorage)
            }
        )
    )
);
